<template>
  <q-dialog :model-value="modelValue" maximized @update:model-value="$emit('update:modelValue', $event)">
    <q-card class="usp-report-card">
      <div class="usp-report-header">
        <div class="usp-report-header__title">
          <q-icon name="assessment" size="22px" color="primary" />
          <div class="usp-report-header__text">
            <div class="usp-report-header__name">Отчёт УСП</div>
            <div v-if="projectName" class="usp-report-header__project">{{ projectName }}</div>
          </div>
        </div>
        <div v-if="!data.loading" class="usp-report-header__stats">
          <div class="usp-report-header__stat">
            <span class="usp-report-header__stat-text">
              По фильтрам <b>{{ fmtInt(data.total || 0) }}</b> {{ pluralRows(data.total || 0) }}
            </span>
          </div>
          <div class="usp-report-header__stat-divider" />
          <div class="usp-report-header__stat usp-report-header__stat--sent" :class="{ 'usp-report-header__stat--warn': isSummaryUnavailable }">
            <q-icon name="check_circle" size="14px" />
            <span>Отпр.: {{ displaySentCountLabel }}</span>
            <q-tooltip v-if="isSummaryUnavailable">
              Не удалось загрузить глобальные итоги (summary). Нажмите «Обновить» или обратитесь к администратору.
            </q-tooltip>
          </div>
          <div class="usp-report-header__stat usp-report-header__stat--pending" :class="{ 'usp-report-header__stat--warn': isSummaryUnavailable }">
            <q-icon name="schedule" size="14px" />
            <span>Не отпр.: {{ displayNotSentCountLabel }}</span>
            <q-tooltip v-if="isSummaryUnavailable">
              Не удалось загрузить глобальные итоги (summary). Нажмите «Обновить» или обратитесь к администратору.
            </q-tooltip>
          </div>
        </div>
        <q-space />
        <div class="usp-report-header__actions">
          <q-btn
            outline no-caps dense color="primary" icon="refresh"
            label="Обновить" :loading="data.refreshing"
            @click="$emit('reload-report')"
          >
            <q-tooltip>Перезагрузить отчёт из базы</q-tooltip>
          </q-btn>
          <q-btn
            v-if="showUspSyncButton"
            outline no-caps dense color="teal" icon="cloud_sync"
            label="Синхр. УСП" :loading="data.syncing"
            @click="onSyncUspClick"
          >
            <q-tooltip>
              Загрузить свежие данные из внешней системы УСП в базу (GetSBSData).
              Долгая операция. «Обновить» только перечитывает уже сохранённые данные.
            </q-tooltip>
          </q-btn>
          <q-btn flat dense round icon="close" v-close-popup>
            <q-tooltip>Закрыть</q-tooltip>
          </q-btn>
        </div>
      </div>

      <div v-if="data.loading" class="usp-report-state">
        <q-spinner size="40px" color="primary" />
        <div class="usp-report-state__text">Загрузка отчёта...</div>
      </div>

      <div v-else class="usp-report-body">
          <div class="usp-report-filters-card">
            <div class="usp-report-filters-card__body">
              <div class="usp-report-filters-toolbar">
                <CustomSelect
                  :options="uspReportContractorOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="Контрагент"
                  :value="data.filterContractor"
                  clearable
                  fullWidth
                  placeholder="Все контрагенты"
                  :useInput="data.filterContractor == null"
                  class="usp-filter-field"
                  @change="onContractorFilterChange"
                />
                <CustomSelect
                  :options="uspReportAgreementOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="Договор"
                  :value="data.filterAgreement"
                  clearable
                  fullWidth
                  placeholder="Все договоры"
                  :disabled="!uspReportAgreementOptions.length && data.filterAgreement == null"
                  :useInput="data.filterAgreement == null"
                  class="usp-filter-field"
                  @change="onAgreementFilterChange"
                >
                  <q-tooltip v-if="!data.loading && !uspReportAgreementOptions.length && data.filterAgreement == null">
                    Договоры не найдены
                  </q-tooltip>
                </CustomSelect>
                <CustomSelect
                  :options="uspReportTypeOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="Тип проекта"
                  :value="data.filterType"
                  clearable
                  fullWidth
                  placeholder="Все типы"
                  :useInput="!data.filterType"
                  class="usp-filter-field"
                  @change="onReportFilterChange('filterType', $event)"
                />
                <CustomSelect
                  :options="uspReportBlockOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="Блок"
                  :value="data.filterBlock"
                  clearable
                  fullWidth
                  placeholder="Все блоки"
                  :useInput="!data.filterBlock"
                  class="usp-filter-field"
                  @change="onReportFilterChange('filterBlock', $event)"
                />
                <CustomSelect
                  :options="uspReportFloorOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="Этаж"
                  :value="data.filterFloor"
                  clearable
                  fullWidth
                  placeholder="Все"
                  :useInput="data.filterFloor == null"
                  class="usp-filter-field"
                  @change="onReportFilterChange('filterFloor', $event)"
                />
                <div class="usp-filter-cell usp-filter-cell--actions">
                  <span class="usp-filter-label-spacer" aria-hidden="true">&nbsp;</span>
                  <div class="usp-filter-control-row usp-filter-actions">
                    <q-btn
                      class="usp-filter-action-btn"
                      outline
                      color="grey-7"
                      no-caps
                      dense
                      icon="filter_alt_off"
                      label="Сбросить"
                      :disable="!hasActiveFilters"
                      @click="uspReportResetAllFilters"
                    />
                    <q-btn
                      class="usp-filter-action-btn"
                      :outline="!isAdvancedFiltersOpen"
                      :unelevated="isAdvancedFiltersOpen"
                      :color="advancedFiltersBtnColor"
                      no-caps
                      dense
                      :icon-right="isAdvancedFiltersOpen ? 'expand_less' : 'expand_more'"
                      label="Фильтры"
                      @click="toggleAdvancedFilters"
                    >
                      <q-badge
                        v-if="hasActiveAdvancedFilters"
                        color="orange"
                        floating
                        rounded
                      />
                    </q-btn>
                    <q-btn
                      class="usp-filter-action-btn"
                      color="positive"
                      unelevated
                      no-caps
                      dense
                      icon="download"
                      label="Excel"
                      :disable="!data.total || data.loading || data.refreshing || data.syncing || exporting"
                      :loading="exporting"
                      @click="exportExcel"
                    />
                  </div>
                </div>
              </div>

              <div
                v-show="isAdvancedFiltersOpen"
                class="usp-report-filters-bottom usp-report-filters-bottom--expanded"
              >
                <CustomInput
                  :value="data.search"
                  label="Поиск"
                  placeholder="Работа, GUID, контрагент, конструктив…"
                  isSearch
                  class="usp-filter-field usp-filter-field--search-bottom"
                  v-debounce:400="onSearchChange"
                  @change_input="(v) => data.search = v"
                />
                <CustomInput
                  :value="data.dateFrom"
                  label="Дата от"
                  type="date"
                  class="usp-filter-field usp-filter-field--date"
                  @change_input="onDateFromChange"
                />
                <CustomInput
                  :value="data.dateTo"
                  label="Дата до"
                  type="date"
                  class="usp-filter-field usp-filter-field--date"
                  @change_input="onDateToChange"
                />
                <CustomSelect
                  :options="uspReportLimitOptions"
                  option-label-key="label"
                  option-value-key="value"
                  label="На странице"
                  :value="data.limit"
                  fullWidth
                  :useInput="false"
                  class="usp-filter-field usp-filter-field--limit"
                  @change="onLimitChange"
                />
                <div class="usp-filter-cell usp-filter-cell--toggle">
                  <span class="usp-filter-label-spacer" aria-hidden="true">&nbsp;</span>
                  <div class="usp-filter-control-row">
                    <CustomToggle
                      :model-value="data.filterNotSent"
                      label="Не отпр."
                      color="orange-8"
                      @update:model-value="onNotSentFilterChange"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!data.loading && !data.refreshing && !data.total && !data.rows.length"
            class="usp-report-state usp-report-state--inline"
          >
            <q-icon name="search_off" size="40px" color="grey-4" />
            <div class="usp-report-state__text">Нет строк по выбранным фильтрам</div>
          </div>

          <div v-if="isSummaryUnavailable" class="usp-report-summary-warn">
            <q-icon name="warning_amber" size="16px" />
            <span>Глобальные итоги (отправлено / не отправлено / суммы) недоступны — данные summary не загрузились с сервера.</span>
          </div>

          <template v-if="data.rows.length">
          <div class="usp-report-table-wrap">
              <table class="usp-report-table">
                <thead>
                  <tr>
                    <th class="text-center usp-th usp-th--sticky-left">Статус</th>
                    <th class="text-left usp-th">Контрагент</th>
                    <th class="text-left usp-th">Тип</th>
                    <th class="text-left usp-th">Блок / Этаж</th>
                    <th class="text-left usp-th">Конструктив / Работа SBS</th>
                    <th class="text-right usp-th">Объём</th>
                    <th class="text-right usp-th">СМР</th>
                    <th class="text-right usp-th">Материал</th>
                    <th class="text-right usp-th usp-th--total">Итого</th>
                    <th class="text-center usp-th">Дата факт</th>
                    <th class="text-center usp-th">Отправка</th>
                    <th class="text-left usp-th">GUID УСП</th>
                    <th class="usp-th--sticky-right usp-th--action" />
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!data.rows.length">
                    <td colspan="13" class="usp-report-table__empty">
                      Нет строк
                    </td>
                  </tr>
                  <tr
                    v-for="r in data.rows"
                    :key="`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}_${r.usp_work_guid}_${r.contractor_id || ''}`"
                    class="usp-report-table__row"
                    :class="r.is_sent ? 'usp-row--sent' : 'usp-row--not-sent'"
                  >
                    <td class="text-center usp-td--sticky-left">
                      <q-badge
                        :color="r.is_sent ? 'green-7' : 'orange-8'"
                        text-color="white"
                        :label="r.is_sent ? 'Отпр.' : 'Не отпр.'"
                        class="usp-status-badge"
                      />
                    </td>
                    <td class="usp-cell-text">{{ r.contractor_name || '—' }}</td>
                    <td class="usp-cell-text">{{ r.project_type_name || '—' }}</td>
                    <td class="usp-cell-stack">
                      <div class="usp-cell-stack__primary">{{ formatBlockLabel(r.block_name) }}</div>
                      <div class="usp-cell-stack__secondary">{{ formatFloorLabel(r.floor_name) }}</div>
                    </td>
                    <td class="usp-cell-stack usp-cell-stack--wide">
                      <div class="usp-cell-stack__primary">{{ r.constructive_name || '—' }}</div>
                      <div class="usp-cell-stack__secondary usp-cell-stack__secondary--work">
                        <span class="usp-work-sbs-line">
                          {{ formatWorkSbsLine(r) }}
                          <q-tooltip
                            v-if="r.work_name"
                            anchor="top middle"
                            self="bottom middle"
                            :offset="[0, 6]"
                            max-width="420px"
                          >
                            {{ formatWorkSbsLine(r) }}
                          </q-tooltip>
                        </span>
                      </div>
                    </td>
                    <td class="text-right mono">{{ fmtKzt(r.fact_value) }}</td>
                    <td class="text-right mono">{{ fmtKzt(r.fact_price) }}</td>
                    <td class="text-right mono">{{ fmtKzt(r.materials_price_total) }}</td>
                    <td class="text-right mono usp-cell-total">{{ fmtKzt((r.fact_price || 0) + (r.materials_price_total || 0)) }}</td>
                    <td class="text-center usp-cell-date">{{ fmtDateRu(r.last_value_date) || '—' }}</td>
                    <td class="text-center usp-cell-date">{{ fmtDateTimeRu(r.sent_at) || '—' }}</td>
                    <td class="usp-cell-guid">
                      <div v-if="r.usp_work_guid" class="row items-center no-wrap">
                        <span
                          class="usp-guid-short"
                          @click.stop="$emit('open-guid-detail', r.usp_work_guid)"
                        >{{ fmtShortGuid(r.usp_work_guid) }}</span>
                        <q-btn flat dense round size="xs" icon="content_copy" color="grey-6"
                          @click.stop="copyToClipboard(r.usp_work_guid)">
                          <q-tooltip>Копировать GUID</q-tooltip>
                        </q-btn>
                        <q-btn flat dense round size="xs" icon="open_in_new" color="grey-6"
                          @click.stop="$emit('open-guid-detail', r.usp_work_guid)">
                          <q-tooltip>Детали УСП</q-tooltip>
                        </q-btn>
                      </div>
                      <span v-else class="text-grey-4">—</span>
                    </td>
                    <td class="usp-td--sticky-right usp-td--action">
                      <q-btn
                        flat
                        dense
                        no-caps
                        size="sm"
                        color="primary"
                        label="Детально"
                        class="usp-detail-btn"
                        @click="$emit('open-work-detail', r)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
          </div>

          <div class="usp-report-footer">
            <div class="usp-report-footer__totals" :class="{ 'usp-report-footer__totals--warn': isSummaryUnavailable }">
              <span class="usp-report-footer__title">
                {{ footerTotalsTitle }}
                <q-tooltip v-if="isSummaryUnavailable">
                  Суммы по фильтрам недоступны — загрузите отчёт повторно или проверьте функцию summary в БД
                </q-tooltip>
              </span>
              <span class="usp-report-footer__item">
                <span class="usp-report-footer__label">Объём</span>
                <span
                  class="usp-report-footer__value"
                  :class="{ 'usp-report-footer__value--unavailable': footerSumFact == null }"
                >{{ footerSumFactLabel }}</span>
              </span>
              <span class="usp-report-footer__item">
                <span class="usp-report-footer__label">СМР</span>
                <span
                  class="usp-report-footer__value"
                  :class="{ 'usp-report-footer__value--unavailable': footerSumSmr == null }"
                >{{ footerSumSmrLabel }}</span>
              </span>
              <span class="usp-report-footer__item">
                <span class="usp-report-footer__label">Материал</span>
                <span
                  class="usp-report-footer__value"
                  :class="{ 'usp-report-footer__value--unavailable': footerSumMat == null }"
                >{{ footerSumMatLabel }}</span>
              </span>
              <span class="usp-report-footer__item usp-report-footer__item--total">
                <span class="usp-report-footer__label">Итого</span>
                <span
                  class="usp-report-footer__value"
                  :class="{ 'usp-report-footer__value--unavailable': footerSumTotal == null }"
                >{{ footerSumTotalLabel }}</span>
              </span>
            </div>
            <q-pagination
              v-if="data.total > data.limit"
              :model-value="data.page"
              :max="uspReportPages"
              :max-pages="7"
              boundary-numbers
              direction-links
              dense
              color="primary"
              @update:model-value="onPageChange"
            />
            <span v-if="data.total > 0" class="usp-report-footer__page">
              {{ fmtInt(uspReportPageFrom) }}–{{ fmtInt(uspReportPageTo) }} из {{ fmtInt(data.total) }}
            </span>
          </div>
          </template>
        </div>
    </q-card>
  </q-dialog>
</template>

<script>
import CustomSelect from '@/components/form/CustomSelect';
import CustomInput from '@/components/form/CustomInput';
import CustomToggle from '@/components/common/CustomToggle.vue';
import { useFormatters } from '../composables/useFormatters';
import { exportUspReportExcel } from '@/views/ProjectPatternStructure/services';

export default {
  name: 'PpmUspReportDialog',
  components: { CustomSelect, CustomInput, CustomToggle },
  props: {
    modelValue: Boolean,
    data: { type: Object, default: () => ({}) },
    projectName: { type: String, default: '' },
    projectGuid: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'open-work-detail', 'open-guid-detail', 'reload-report', 'sync-usp', 'filter-change', 'page-change', 'limit-change'],
  data() {
    return {
      showUspSyncButton: false,
      exporting: false,
      isAdvancedFiltersOpen: localStorage.getItem('usp_report_advanced_filters_open') === '1',
    };
  },
  setup() {
    const { fmtKzt, fmtInt, fmtDateRu, fmtDateTimeRu } = useFormatters();
    return { fmtKzt, fmtInt, fmtDateRu, fmtDateTimeRu };
  },
  computed: {
    uspReportTypeOptions() {
      return (this.data.filterOptions?.types || []).map((v) => ({ label: v, value: v }));
    },
    uspReportBlockOptions() {
      return (this.data.filterOptions?.blocks || []).map((v) => ({
        label: this.formatBlockLabel(v),
        value: v,
      }));
    },
    uspReportFloorOptions() {
      return (this.data.filterOptions?.floors || []).map((v) => ({
        label: `Этаж ${v}`,
        value: v,
      }));
    },
    uspReportContractorOptions() {
      return (this.data.contractorOptions || []).map((c) => ({
        label: c.contractor_name,
        value: c.contractor_id,
      }));
    },
    uspReportAgreementOptions() {
      return (this.data.agreementOptions || []).map((a) => ({
        label: a.agreement_num,
        value: a.project_agreement_id,
      }));
    },
    uspReportLimitOptions() {
      return [50, 100, 200].map((v) => ({ label: String(v), value: v }));
    },
    hasReportFilters() {
      return !!(this.data.search || this.data.dateFrom || this.data.dateTo
        || this.data.filterType || this.data.filterBlock
        || this.data.filterFloor != null || this.data.filterNotSent);
    },
    hasServerFilters() {
      return this.data.filterContractor != null || this.data.filterAgreement != null;
    },
    hasActiveFilters() {
      return this.hasServerFilters || this.hasReportFilters
        || (this.data.limit && this.data.limit !== 50);
    },
    hasActiveAdvancedFilters() {
      return !!(this.data.search || this.data.filterNotSent
        || this.data.dateFrom || this.data.dateTo
        || (this.data.limit && this.data.limit !== 50));
    },
    advancedFiltersBtnColor() {
      if (this.isAdvancedFiltersOpen) return 'primary';
      if (this.hasActiveAdvancedFilters) return 'primary';
      return 'grey-7';
    },
    isSummaryGlobal() {
      return this.data.summaryAvailable === true && !!this.data.summary;
    },
    isSummaryUnavailable() {
      return !this.data.loading
        && !this.data.refreshing
        && !this.isSummaryGlobal;
    },
    displaySentCount() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.sent_count ?? 0;
    },
    displayNotSentCount() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.not_sent_count ?? 0;
    },
    displaySentCountLabel() {
      return this.displaySentCount == null ? '—' : this.fmtInt(this.displaySentCount);
    },
    displayNotSentCountLabel() {
      return this.displayNotSentCount == null ? '—' : this.fmtInt(this.displayNotSentCount);
    },
    footerTotalsTitle() {
      if (this.isSummaryGlobal) return 'Итого по фильтрам';
      return 'Итого по фильтрам недоступно';
    },
    footerSumFact() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.sum_fact_value ?? 0;
    },
    footerSumSmr() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.sum_fact_price ?? 0;
    },
    footerSumMat() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.sum_materials_price_total ?? 0;
    },
    footerSumTotal() {
      if (!this.isSummaryGlobal) return null;
      return this.data.summary.sum_total ?? 0;
    },
    footerSumFactLabel() {
      return this.footerSumFact == null ? '—' : this.fmtKzt(this.footerSumFact);
    },
    footerSumSmrLabel() {
      return this.footerSumSmr == null ? '—' : this.fmtKzt(this.footerSumSmr);
    },
    footerSumMatLabel() {
      return this.footerSumMat == null ? '—' : this.fmtKzt(this.footerSumMat);
    },
    footerSumTotalLabel() {
      return this.footerSumTotal == null ? '—' : this.fmtKzt(this.footerSumTotal);
    },
    uspReportPages() {
      const limit = this.data.limit || 50;
      const total = this.data.total || 0;
      return Math.max(1, Math.ceil(total / limit));
    },
    uspReportPageFrom() {
      if (!this.data.total) return 0;
      return (this.data.page - 1) * (this.data.limit || 50) + 1;
    },
    uspReportPageTo() {
      if (!this.data.total) return 0;
      return Math.min(this.data.page * (this.data.limit || 50), this.data.total);
    },
  },
  watch: {
    hasActiveAdvancedFilters(active) {
      if (active) this.isAdvancedFiltersOpen = true;
    },
  },
  methods: {
    pluralRows(n) {
      const num = Math.abs(Math.trunc(Number(n) || 0));
      const mod10 = num % 10;
      const mod100 = num % 100;
      if (mod100 >= 11 && mod100 <= 14) return 'строк';
      if (mod10 === 1) return 'строка';
      if (mod10 >= 2 && mod10 <= 4) return 'строки';
      return 'строк';
    },
    formatBlockLabel(blockName) {
      if (blockName == null || blockName === '') return '—';
      const value = String(blockName).trim();
      if (/^блок\b/i.test(value)) return value;
      return `Блок ${value}`;
    },
    formatFloorLabel(floorName) {
      if (floorName == null || floorName === '') return '—';
      return `${floorName} этаж`;
    },
    formatWorkSbsLine(row) {
      const id = row.work_sbs_id;
      const name = row.work_name;
      if (id != null && id !== '' && name) return `${id} / ${name}`;
      if (id != null && id !== '') return String(id);
      if (name) return name;
      return '—';
    },
    toggleAdvancedFilters() {
      this.isAdvancedFiltersOpen = !this.isAdvancedFiltersOpen;
      localStorage.setItem('usp_report_advanced_filters_open', this.isAdvancedFiltersOpen ? '1' : '0');
    },
    uspReportResetAllFilters() {
      this.data.filterContractor = null;
      this.data.filterAgreement = null;
      this.data.search = '';
      this.data.dateFrom = '';
      this.data.dateTo = '';
      this.data.filterType = null;
      this.data.filterBlock = null;
      this.data.filterFloor = null;
      this.data.filterNotSent = false;
      this.data.limit = 50;
      localStorage.setItem('usp_report_limit', '50');
      this.$emit('filter-change', true);
    },
    onReportFilterChange(field, val) {
      this.data[field] = val;
      this.$emit('filter-change', false);
    },
    onSearchChange(val) {
      this.data.search = val || '';
      this.$emit('filter-change', false);
    },
    onDateFromChange(val) {
      this.data.dateFrom = val || '';
      if (this.data.dateFrom && this.data.dateTo && this.data.dateFrom > this.data.dateTo) return;
      this.$emit('filter-change', false);
    },
    onDateToChange(val) {
      this.data.dateTo = val || '';
      if (this.data.dateFrom && this.data.dateTo && this.data.dateFrom > this.data.dateTo) return;
      this.$emit('filter-change', false);
    },
    onNotSentFilterChange(val) {
      this.data.filterNotSent = val;
      this.$emit('filter-change', false);
    },
    onLimitChange(val) {
      this.data.limit = val;
      this.$emit('limit-change', val);
    },
    onSyncUspClick() {
      this.$q.dialog({
        title: 'Синхронизация с УСП',
        message: 'Загрузить свежие данные из системы УСП? Операция может занять некоторое время.',
        cancel: { label: 'Отмена', flat: true },
        ok: { label: 'Синхронизировать', color: 'primary' },
        persistent: true,
      }).onOk(() => {
        this.$emit('sync-usp');
      });
    },
    fmtShortGuid(guid) {
      if (!guid || guid.length < 12) return guid || '—';
      return `${guid.slice(0, 8)}…${guid.slice(-6)}`;
    },
    onPageChange(page) {
      this.$emit('page-change', page);
    },
    onContractorFilterChange(val) {
      this.data.filterContractor = val;
      if (val != null && this.data.filterAgreement != null) {
        const agreement = (this.data.agreementOptions || []).find(
          (a) => a.project_agreement_id == this.data.filterAgreement,
        );
        if (agreement && agreement.contractor_id != null && agreement.contractor_id != val) {
          this.data.filterAgreement = null;
        }
      }
      this.$emit('filter-change', true);
    },
    onAgreementFilterChange(val) {
      this.data.filterAgreement = val;
      if (val != null) {
        const agreement = (this.data.agreementOptions || []).find(
          (a) => a.project_agreement_id == val,
        );
        if (agreement?.contractor_id != null) {
          this.data.filterContractor = agreement.contractor_id;
        }
      }
      this.$emit('filter-change', true);
    },
    buildExportParams() {
      return {
        projectName: this.projectName || undefined,
        contractorId: this.data.filterContractor,
        projectAgreementId: this.data.filterAgreement,
        projectTypeName: this.data.filterType || undefined,
        blockName: this.data.filterBlock || undefined,
        floorName: this.data.filterFloor != null && this.data.filterFloor !== ''
          ? String(this.data.filterFloor)
          : undefined,
        search: (this.data.search || '').trim() || undefined,
        dateFrom: this.data.dateFrom || undefined,
        dateTo: this.data.dateTo || undefined,
        isNotSentOnly: this.data.filterNotSent || undefined,
      };
    },
    async exportExcel() {
      if (!this.projectGuid || !this.data.total || this.exporting || this.data.loading || this.data.refreshing) return;
      this.exporting = true;
      try {
        await exportUspReportExcel(this.projectGuid, this.buildExportParams());
      } finally {
        this.exporting = false;
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {
        this.$q.notify({ message: 'Не удалось скопировать', color: 'negative', timeout: 1500, position: 'top' });
      });
    },
  },
};
</script>

<style scoped>
.usp-report-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f4f6f8;
}

.usp-report-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.usp-report-header__title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.usp-report-header__text {
  min-width: 0;
}

.usp-report-header__name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
}

.usp-report-header__project {
  font-size: 13px;
  color: #475569;
  line-height: 1.3;
  max-width: 560px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.usp-report-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.usp-report-header__stats {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 2px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.usp-report-header__stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #334155;
  white-space: nowrap;
}

.usp-report-header__stat--sent {
  color: #15803d;
}

.usp-report-header__stat--pending {
  color: #c2410c;
}

.usp-report-header__stat--warn {
  color: #b45309 !important;
}

.usp-report-summary-warn {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-size: 12px;
  line-height: 1.4;
}

.usp-report-header__stat-text {
  font-size: 12px;
  color: #334155;
}

.usp-report-header__stat-text b {
  font-weight: 700;
  color: #0f172a;
}

.usp-report-header__stat-divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
}

.usp-report-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #94a3b8;
}

.usp-report-state__text {
  font-size: 14px;
}

.usp-report-state--inline {
  flex: 1;
  min-height: 120px;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 8px;
}

.usp-report-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 10px 12px 0;
  gap: 8px;
}

.usp-report-filters-card {
  margin-bottom: 0;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  flex-shrink: 0;
}

.usp-report-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.usp-report-filters-toolbar {
  display: grid;
  grid-template-columns: 1.25fr 1.1fr 1fr 0.72fr 0.52fr auto;
  align-items: start;
  gap: 8px;
}

.usp-filter-label-spacer {
  display: block;
  height: 15px;
  margin-bottom: 4px;
  overflow: hidden;
  visibility: hidden;
  font-size: 11px;
  line-height: 15px;
}

.usp-filter-control-row {
  height: 32px;
  display: flex;
  align-items: center;
}

.usp-filter-cell--action .usp-filter-control-row,
.usp-filter-cell--actions .usp-filter-control-row {
  justify-content: stretch;
}

.usp-filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.usp-filter-cell--action .usp-filter-action-btn,
.usp-filter-actions .usp-filter-action-btn {
  width: 118px;
  min-width: 118px;
}

.usp-filter-cell--toggle .usp-filter-control-row :deep(.q-toggle) {
  height: 32px;
  min-height: 32px;
  margin: 0;
}

.usp-filter-cell--toggle .usp-filter-control-row :deep(.q-toggle__label) {
  font-size: 12px;
}

.usp-report-filters-bottom {
  display: grid;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eef2f7;
  align-items: start;
}

.usp-report-filters-bottom--expanded {
  grid-template-columns: 1.2fr 0.68fr 0.68fr 0.48fr auto;
}

.usp-filter-field {
  min-width: 0;
  width: 100%;
}

.usp-filter-field--search-bottom {
  min-width: 0;
}

.usp-filter-field--date {
  min-width: 0;
}

.usp-filter-field--limit {
  min-width: 0;
}

.usp-report-filters-card .usp-filter-field :deep(.custom-select__label),
.usp-report-filters-card .usp-filter-field :deep(.customInput__label) {
  display: block;
  height: 15px;
  font-size: 11px;
  line-height: 15px;
  margin-bottom: 4px !important;
  color: #64748b;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field__bottom) {
  display: none !important;
  min-height: 0 !important;
  padding: 0 !important;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field__control) {
  min-height: 32px !important;
  height: 32px !important;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field__control-container) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field__marginal) {
  height: 32px;
}

.usp-report-filters-card .usp-filter-field :deep(.q-field__native),
.usp-report-filters-card .usp-filter-field :deep(.q-field__input) {
  font-size: 12px;
  min-height: 32px;
  line-height: 32px;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.usp-report-filters-card .usp-filter-field :deep(.custom-select__wrapper),
.usp-report-filters-card .usp-filter-field :deep(.q-field) {
  width: 100%;
}

.usp-report-filters-card .usp-filter-action-btn {
  font-size: 12px;
  min-height: 32px !important;
  height: 32px !important;
  min-width: 0;
  margin: 0 !important;
  padding: 0 10px;
}

@media (max-width: 1400px) {
  .usp-report-filters-toolbar {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .usp-report-filters-bottom--expanded {
    grid-template-columns: 1fr 0.75fr 0.75fr 0.55fr auto;
  }
}

@media (max-width: 1100px) {
  .usp-report-header__stats {
    display: none;
  }

  .usp-report-filters-toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .usp-report-filters-bottom--expanded {
    grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  }

  .usp-report-filters-bottom--expanded .usp-filter-field--search-bottom {
    grid-column: 1 / -1;
  }
}

@media (max-width: 720px) {
  .usp-report-filters-toolbar,
  .usp-report-filters-bottom,
  .usp-report-filters-bottom--expanded {
    grid-template-columns: 1fr;
  }
}

.usp-report-table-wrap {
  flex: 1;
  min-height: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.usp-report-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.usp-report-table thead tr {
  background: #f8fafc;
}

.usp-report-table th {
  position: sticky;
  top: 0;
  z-index: 4;
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  padding: 6px 8px;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
}

.usp-report-table th.usp-th--sticky-left,
.usp-report-table td.usp-td--sticky-left {
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 88px;
}

.usp-report-table th.usp-th--sticky-left {
  top: 0;
  left: 0;
  z-index: 6;
  background: #f8fafc;
}

.usp-report-table td.usp-td--sticky-left {
  background: inherit;
  box-shadow: 2px 0 6px -2px rgba(15, 23, 42, 0.1);
}

.usp-report-table th.usp-th--sticky-right,
.usp-report-table td.usp-td--sticky-right {
  position: sticky;
  right: 0;
  z-index: 3;
  background: inherit;
  box-shadow: -2px 0 6px -2px rgba(15, 23, 42, 0.1);
}

.usp-report-table th.usp-th--action,
.usp-report-table td.usp-td--action {
  min-width: 88px;
  padding: 4px 8px;
  text-align: center;
}

.usp-report-table th.usp-th--sticky-right {
  top: 0;
  right: 0;
  z-index: 6;
  background: #f8fafc;
}

.usp-report-table th.usp-th {
  user-select: none;
}

.usp-report-table th.usp-th--total {
  color: #1d4ed8;
}

.usp-report-table td {
  font-size: 12px;
  padding: 5px 8px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.usp-report-table__row:hover {
  background: #eff6ff !important;
}

.usp-report-table__row:hover td.usp-td--sticky-left,
.usp-report-table__row:hover td.usp-td--sticky-right {
  background: #eff6ff !important;
}

.usp-report-table__empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px !important;
}

.usp-row--not-sent {
  box-shadow: inset 3px 0 0 #f59e0b;
}

.usp-status-badge {
  font-size: 10px;
  padding: 2px 6px;
}

.usp-cell-text {
  max-width: 160px;
  white-space: normal;
  line-height: 1.35;
  color: #334155;
}

.usp-cell-stack {
  max-width: 150px;
  line-height: 1.35;
}

.usp-cell-stack--wide {
  min-width: 280px;
  max-width: 380px;
}

.usp-cell-stack__primary {
  font-weight: 600;
  color: #334155;
}

.usp-cell-stack__secondary {
  margin-top: 2px;
  font-size: 11px;
  color: #94a3b8;
}

.usp-cell-stack__secondary--work {
  overflow: hidden;
}

.usp-work-sbs-line {
  display: -webkit-box;
  font-size: 11px;
  line-height: 1.4;
  color: #94a3b8;
  white-space: normal;
  word-break: break-word;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}

.usp-detail-btn {
  font-size: 12px;
  padding: 0 4px;
}

.usp-cell-total {
  color: #1d4ed8;
  font-weight: 600;
}

.usp-cell-date {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

.usp-cell-guid {
  max-width: 200px;
}

.usp-guid-short {
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  white-space: nowrap;
}

.usp-report-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  margin: 0 0 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  flex-shrink: 0;
}

.usp-report-footer__totals {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
}

.usp-report-footer__title {
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.usp-report-footer__item {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.usp-report-footer__label {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;

  &::after {
    content: ':';
  }
}

.usp-report-footer__value {
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.usp-report-footer__item--total .usp-report-footer__label {
  color: #94a3b8;
  font-weight: 500;
}

.usp-report-footer__item--total .usp-report-footer__value {
  color: #1d4ed8;
  font-weight: 700;
}

.usp-report-footer__totals--warn .usp-report-footer__title {
  color: #b45309;
}

.usp-report-footer__value--unavailable {
  color: #94a3b8 !important;
  font-weight: 600 !important;
}

.usp-report-footer__page {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}
</style>
